import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";

import PageHeader from "../../components/shared/PageHeader";
import UserForm from "../../components/users/UserForm";
import BlueButton from "../../components/shared/BlueButton";
import LoadingModal from "../../components/shared/LoadingModal";

import { createUser, getUser, updateUser } from "../../api/user";
import { getFileUrl } from "../../api/file";
import { formatDate } from "../../lib/utils";

export default function UserFormView() {
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [formInstance, setFormInstance] = useState(null);
    const [userData, setUserData] = useState(null);

    const { userId } = useParams();
    const navigate = useNavigate();

    const handleSubmit = async () => {
        setLoading(true)
        try {
            const input = await formInstance?.validateFields();
            const rateData = { rate: input.rate, rateBy: input.rateBy }
            if (input.password !== input.confirm) {
                message.error("Password does not match");
                throw new Error("Password and confirm password must be the same");
            }

            const avatar =
                input.avatar &&
                    input.avatar.length > 0
                    ? input.avatar.map((file) => {
                        if (!file.response) return file;
                        return file.response.file;
                    })[0]
                    : userData && userData.avatar;



            const data = {
                firstName: input.firstName,
                lastName: input.lastName,
                role: input.role,
                email: input.email,
                phone: input.phone,
                rate: rateData,
                password: input.password,
                attachments: input.attachments,
                license: input.license,
                ticket: input.ticket,
                avatar
            }


            if (editing) {
                await updateUser({ ...data, id: userId }).then(() => {
                    message.success("User updated successfully");
                })
            } else {
                await createUser({ ...data }).then(() => {
                    message.success("User created successfully");
                })
                    .catch(e => {
                        message.error("Couldn't create user.");
                    })
            }
            setLoading(false);
            navigate("/users")

        } catch (err) {
            console.error(err);
            setLoading(false);
        }

    }

    const fetchUser = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getUser(userId);
            const activeAttachments = result.attachments && result.attachments.filter(attachment => attachment.isActive);
            setUserData(result)
            const values = {
                ...result,
                avatar: result.avatar && [{
                    ...result.avatar,
                    uid: result.avatar.id,
                    name: result.avatar.name,
                    status: "done",
                    url: getFileUrl(result.avatar),
                }],
                password: undefined,
                rate: result.rate?.rate,
                rateBy: result.rate?.rateBy,
                attachments: result.attachments && activeAttachments.map((attachment) => {
                    return {
                        ...attachment,
                        fileAttachment: {
                            ...attachment.fileAttachment,
                            file: {
                                ...attachment.fileAttachment.file,
                                uid: attachment.fileAttachment.id,
                                status: "done",
                                name: attachment.fileAttachment.file.originalName,
                                url: getFileUrl(attachment.fileAttachment.file),
                            }
                        }
                    };
                })
            }
            formInstance?.setFieldsValue(values)
            if (result.attachments && result.attachments.length > 0) {
                updateDocuments(result)
            }
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    }, [formInstance, userId])


    const updateDocuments = (user) => {
        const docs = [];
        const activeDocuments = user.attachments.filter(doc => {
            return doc.isActive
        })
        activeDocuments.forEach(
            (doc, index) => {
                docs.push(
                    {
                        key: `${doc.id + index + 1}`,
                        label: "Document Name",
                        children: doc.fileAttachment.name
                    },
                    {
                        key: `${doc.id + index + 2}`,
                        label: "Expiry Date",
                        children: formatDate(doc.fileAttachment.expireAt),
                    },
                    {
                        key: `${doc.id + index + 3}`,
                        label: "Document",
                        children: doc.fileAttachment.file.name
                    }
                )
            }
        );
    }

    useEffect(() => {
        if (userId) {
            setEditing(true)
            fetchUser()
        }
    }, [userId, fetchUser])

    return (
        <main className="space-y-6 min-h-full flex flex-col">
            <PageHeader title={editing ? "Update User" : "Create User"} home="Users" route="/users" />

            <div className="bg-white ml-6 p-6 relative">
                <div >
                    <UserForm onFormInstanceReady={(instance) => setFormInstance(instance)} isEditing={Boolean(userId)} />
                </div>

                <BlueButton type="primary" onClick={handleSubmit}>{editing ? "Update" : "Create"} User</BlueButton>

                {loading && <LoadingModal />}
            </div>

        </main>
    )
}
